import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    FREE_TRIAL_ENABLED,
    SEND_MESSAGE_ID,
    SEND_REJECTION_REASON,
    STORAGE_KEY_ACCESS_TOKEN,
    STORAGE_KEY_ACTIVE_CHAT,
    STORE_PAYMENT_MODAL_TYPE,
    UNREG_CHATS_ID,
    UNREG_CHATS_MAX,
    USER_CHAT_ROLE
} from '../../const';
import chatHistoryApi from '../../service/Api/chatApi';
import chatApi from '../../service/Api/chatApi';
import storageService from '../../service/StorageService';
import { gaEvents } from '../../utils/analytics';
import { updateResendData } from '../../utils/helper';
import { fetchUser, getUserPlan } from './authSlice';
import { setPaymentModalType, tryToCreateOffer } from './modalsSlice';

const initialState = {
    success: true,
    chats: {
        list: [],
        hasMore: true,
        currentPage: 1,
        reloading: false,
        chatDeleteModalConfig: {
            isOpened: false,
            chatId: '',
            chatName: ''
        }
    },
    loading: true,
    error: null,
    activeChat: {
        chatId: null,
        loading: false,
        submitBtnAnimation: false,
        userConnecting: false,
        error: null,
        dateCreated: '',
        name: '',
        messages: [],
        // True once `fetchChatMessages.fulfilled` has fired at least once
        // for the current chat. Distinguishes "genuinely empty chat" from
        // "haven't loaded yet" — used by the v2 ChatList HomeCards gate so
        // the empty-state cards don't flash before the initial fetch
        // resolves. Reset to false in `setActiveChatId` (chat switch) and
        // `fetchChatMessages.pending` when reloading.
        hasLoadedMessages: false,
        reloading: false,
        hasMore: true,
        currentPage: 1,
        userID: null,
        advisor: null,
        messagesForTypewriter: [],
        typewriterInProgress: false,
        anchorMessageId: '',
        chatHeaderCounter: 0,
        attemptsToChatWithExceededLimit: 0,
        // HORO-10679: the in-chat message-pack offer, raised when a free-plan
        // customer closes the out-of-messages paywall. `chatId` scopes it to
        // the conversation it was raised in; `at` is bumped on every later
        // close so the impression fires again even though nothing about the
        // block moves (it is pinned to the bottom of the thread). Session
        // state on purpose — nothing persists it, so a reload clears it.
        messagePackOffer: null,
        // The "20 messages added" line in the thread. Hydrated from storage on
        // arrival (redux does not survive Checkout's full-page return) and then
        // driven from here, so sending the next message can retire it.
        messagePackReceipt: null,
        // Confirms the top-up landed, on arrival back in the conversation.
        messagePackToast: false,
        showPaymentIssueNotification: false,
        showUserLimits: true,
        isWithTypewriter: true,
        // True between dispatching `requestTarotFollowup` and the
        // response landing — drives a typing-dots placeholder so the
        // user sees that the advisor is "writing" a follow-up after
        // they close the tarot reveal modal.
        tarotFollowupPending: false,
        unreadMessagesTriggerId: null,
        unreadMessages: [],
        wasRemovedByOwner: false,
        chatMembersInfo: {
            loading: true,
            data: null
        },
        lastMessageInChatId: null,
        showReconnectAnimation: false,
        loadedImage: {
            loading: false,
            data: null
        }
    },
    inviteLink: null,
    scrollTrigger: '',
    showMultyChatInvationModal: false,
    updateRemoveChatModal: {
        isOpen: false,
        advisorAlias: null
    }
};

export const fetchChats = createAsyncThunk('chats/fetchChats', async (params, { rejectWithValue }) => {
    try {
        const data = await chatHistoryApi.getChatsHistory(params.page);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const startNewChat = createAsyncThunk('chats/testNewChat', async (params, { rejectWithValue }) => {
    try {
        const data = await chatApi.initChat();
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.response?.status);
    }
});

// Start a new chat routed to the user's preferred default advisor. Reads
// `auth.user.defaultAdvisorAlias` and dispatches the alias-flavored chat
// init under the hood; falls back to a plain `startNewChat` (server-side
// advisor default) when:
//   - chat v2 is OFF — default-advisor preference is a v2-only concept;
//     v1 users should always land on Veda regardless of stored preference,
//     and
//   - the user has no preference set, OR
//   - the alias is the system default ('veda').
// Existing chats are unaffected — this only changes which advisor opens
// *new* chats.
//
// Strategy reference: "Veda × Ophelia — Advisor flow Strategy" — the
// "default advisor" concept is what changes who hosts new sessions.
export const startChatWithDefaultAdvisor = createAsyncThunk(
    'chats/startChatWithDefaultAdvisor',
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState();
            const alias = state?.auth?.user?.defaultAdvisorAlias;
            if (!alias || alias === 'veda') {
                const result = await dispatch(startNewChat()).unwrap();
                return result;
            }
            const result = await dispatch(startChatWithAdvisor(`alias/${alias}`)).unwrap();
            return result;
        } catch (error) {
            return rejectWithValue(error?.response?.status || error);
        }
    }
);

export const startChatWithAdvisor = createAsyncThunk('chats/startChatWithAdvisorById', async (params, { rejectWithValue }) => {
    try {
        const data = await chatApi.initChatWithAdvisor(params);
        return data.data;
    } catch (error) {
        // Forward enough context that callers can show a meaningful toast
        // ("Advisor not found" / "Something went wrong" / etc.) instead of
        // a bare status code. Same shape as `sendNewChatMessage`'s reject
        // payload.
        return rejectWithValue({
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message
        });
    }
});

export const startV2Chat = createAsyncThunk('chats/startV2Chat', async ({ advisorAlias, content }, { rejectWithValue }) => {
    try {
        const data = await chatApi.startV2Chat({ advisorAlias, content });
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.response?.data || error?.response?.status);
    }
});

export const startChatWithAdvisorByActivities = createAsyncThunk(
    'chats/startChatWithAdvisorByActivities',
    async (params, { rejectedWithValue }) => {
        try {
            const data = await chatApi.initChatWithAdvisorByActivities(params);
            return data.data;
        } catch (error) {
            return rejectedWithValue(error);
        }
    }
);

export const fetchChatMessages = createAsyncThunk('chats/fetchChatMessages', async (params, { rejectedWithValue }) => {
    try {
        const data = await chatApi.getChatMessages(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error?.message);
    }
});

export const connectUserToChat = createAsyncThunk('chats/connectUserToChat', async (params, { rejectedWithValue }) => {
    try {
        const data = await chatApi.connectToChat(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error?.message);
    }
});

export const sendNewChatMessage = createAsyncThunk('chats/sendNewChatMessage', async (params, { rejectWithValue }) => {
    try {
        // HORO-10370: pass the message content so the analytics layer can
        // compute character_count and contains_question_mark per-event.
        gaEvents.eventMessageSend(params.content);
        const normalisedAttachments = params.attachments.map((item) => {
            const newObj = { ...item };
            if (newObj.hasOwnProperty('type')) {
                delete newObj['type'];
            }
            return newObj;
        });
        const formattedParams = { chatId: params.chatId, content: params.content, attachments: normalisedAttachments };
        // Multi-advisor handoff: when supplied (only by the chat page today), forward
        // to chatApi which adds it to the request body. v1 callers omit it and the
        // outgoing payload is unchanged.
        if (params.targetAdvisorAlias) {
            formattedParams.targetAdvisorAlias = params.targetAdvisorAlias;
        }
        // Tarot reading context, forwarded so the backend can persist the
        // drawn cards on the assistant response and inject them into the
        // prompt. Only the chat page's tarot flow supplies this.
        if (params.tarot) {
            formattedParams.tarot = params.tarot;
        }
        // Daily horoscope request — backend pulls the feed and asks Veda
        // to paraphrase. Only the chat page's Horoscope pill supplies this.
        if (params.horoscope) {
            formattedParams.horoscope = params.horoscope;
        }
        // HORO-10327: anon tarot credit window header; auth users gate
        // off the user document and don't need this. Forwarded
        // verbatim to chatApi which sets the `x-anon-tarot-credits`
        // request header.
        if (params.anonTarotCreditsHeader) {
            formattedParams.anonTarotCreditsHeader = params.anonTarotCreditsHeader;
        }
        // v2 sends pass this to ask the backend to collapse multi-paragraph
        // AI responses into a single Message (with `\n\n` separators)
        // instead of one Message per paragraph. v1 callers omit it.
        if (params.collapseParagraph) {
            formattedParams.collapseParagraph = params.collapseParagraph;
        }
        // HORO-10459: set by the v3 cold start on the first message of a chat,
        // where the advisor's intro was shown client-side and so isn't in the
        // thread for the model to see. Suppresses a duplicate self-introduction.
        if (params.skipIntro) {
            formattedParams.skipIntro = true;
        }
        // HORO-10360 (A7): send → assistant message received, for the latency
        // comparison across birth chart CTAs (see bc / chat_reply_latency).
        const sendStartedAt = Date.now();
        const data = await chatApi.sendChatMessage(formattedParams);
        // HORO-10370: backend returns `answer` as an array containing the
        // user's echoed message + one or more assistant replies (one per
        // paragraph when collapseParagraph=false). Filter to assistant
        // messages only so response_token_count + response_contains_followup_question
        // reflect just the AI's reply.
        const answerMessages = Array.isArray(data?.data?.answer) ? data.data.answer : [];
        const assistantMessages = answerMessages.filter((m) => m?.role === 'assistant');
        const combinedContent = assistantMessages.map((m) => m?.content || '').join('\n\n');
        const totalTokens = assistantMessages.reduce((sum, m) => sum + (m?.tokens || 0), 0);
        gaEvents.eventMessageReceived(combinedContent, totalTokens);
        gaEvents.chatReplyLatency(Date.now() - sendStartedAt, assistantMessages.find((m) => m?.payload?.cta?.kind)?.payload?.cta?.kind);
        return data.data;
    } catch (error) {
        // HORO-10327: forward status + response data so callers can
        // distinguish a 429 tarot-limit-reached from generic failures
        // (and read the `data.cta` upsell payload). Existing call sites
        // only inspect the truthy/falsy nature of the action — no
        // breakage there. Pre-existing typo `rejectedWithValue` was a
        // no-op (undefined); fixing in the same change.
        return rejectWithValue({
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
            // HORO-10384: classify the failure so the chat UI can decide
            // whether to show the inline retry bubble (timeout / network)
            // or fall through to the existing toast paths (4xx / 5xx
            // surfaced by ResponseService).
            reason: classifySendRejection(error)
        });
    }
});

// HORO-10384: discriminate the backend's per-request timeout (HTTP 408 with
// `{ data: { reason: 'timeout' } }` thrown from `addMessageIntoChat` when
// our AbortController fires) from network drops and other server errors.
// The chat UI's inline retry bubble only fires for timeout / network —
// other server rejections already surface their own UX via ResponseService
// toasts and per-status branching in callers (e.g. 429 tarot-limit-reached
// opens the upsell modal).
function classifySendRejection(error) {
    const status = error?.response?.status;
    if (status === 408) {
        return SEND_REJECTION_REASON.TIMEOUT;
    }
    if (status) {
        return SEND_REJECTION_REASON.SERVER;
    }
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
        return SEND_REJECTION_REASON.NETWORK;
    }
    return SEND_REJECTION_REASON.UNKNOWN;
}

// Tarot follow-up — fires when the user closes the live reveal overlay.
// Server generates one Veda-voiced message that builds on the reading.
// The fulfilled reducer below routes the response through the typewriter
// queue (`messagesForTypewriter`) so it types in like Veda's other AI
// replies — different from a WS-driven message which would land instantly.
export const requestTarotFollowup = createAsyncThunk('chats/requestTarotFollowup', async (chatId, { rejectWithValue }) => {
    try {
        const data = await chatApi.requestTarotFollowup(chatId);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

// HORO-10360: same idea as the tarot follow-up, for the birth chart — one
// advisor message after the seeker closes their chart. It reuses the tarot
// follow-up's pending flag (typing-dots bubble) and typewriter queue, so it
// reads exactly like Veda's other replies.
export const requestBirthChartFollowup = createAsyncThunk('chats/requestBirthChartFollowup', async (chatId, { rejectWithValue }) => {
    try {
        const data = await chatApi.requestBirthChartFollowup(chatId);
        return data.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteChat = createAsyncThunk('chats/deleteChat', async (chatId, { rejectedWithValue }) => {
    try {
        const response = await chatApi.deleteChat(chatId);
        return { status: response.data, chatId };
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const editChatName = createAsyncThunk('chats/editChatName', async ({ chatId, newChatName }, { rejectedWithValue }) => {
    try {
        const response = await chatApi.editChatName(chatId, newChatName);
        return response.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const loadImageFile = createAsyncThunk('chats/loadImageFile', async (params, { rejectWithValue }) => {
    try {
        const response = await chatApi.loadImage(params);
        return response.data;
    } catch (error) {
        console.log('error', error);
        return rejectWithValue(error.message);
    }
});

export const showPaymentModalWithOfferActivation = createAsyncThunk(
    'chats/showPaymentModalWithOfferActivation',
    async (params, { rejectWithValue, getState, dispatch }) => {
        try {
            const state = getState();
            dispatch(tryToCreateOffer({ source: params?.source }));

            const userPlan = getUserPlan(state);
            // With the trial off, a customer who hasn't trialled is offered the
            // subscription rather than the trial variant of the modal.
            const showTrialVariant = FREE_TRIAL_ENABLED && !userPlan.isTrial;
            dispatch(setPaymentModalType(showTrialVariant ? STORE_PAYMENT_MODAL_TYPE.trial : STORE_PAYMENT_MODAL_TYPE.subscription));
        } catch (error) {
            console.log('error', error);
            return rejectWithValue(error.message);
        }
    }
);

export const messageAddedInChat = createAction('chat/messageAddedInChat');
export const chatWasRemovedByOwner = createAction('chat/chatWasRemovedByOwner');
export const resetUnreadMessagesTriggerId = createAction('chat/resetUnreadMessagesTriggerId');
export const setMultyChatInvationModalVisibility = createAction('chat/setMultyChatInvationModalVisibility');
export const setUpdateRemoveChatModal = createAction('chat/setUpdateRemoveChatModal');
export const resetImageFile = createAction('chat/resetImageFile');
export const attemptToChatWithExceededLimit = createAction('chat/attemptToChatWithExceededLimit');
// HORO-10679: raised by the out-of-messages paywall's close button, never by
// the modal closing on its own — see MessageLimitPaywall.
export const showMessagePackOffer = createAction('chat/showMessagePackOffer');
export const clearMessagePackOffer = createAction('chat/clearMessagePackOffer');
export const setMessagePackToast = createAction('chat/setMessagePackToast');
export const setMessagePackReceipt = createAction('chat/setMessagePackReceipt');
export const clearMessagePackReceipt = createAction('chat/clearMessagePackReceipt');

export const getChatsState = (store) => store.chats.chats;
export const getActiveChatState = (store) => store.chats.activeChat;
export const getLastMessageInChatId = (store) => store.chats.activeChat.lastMessageInChatId;
export const getActiveChatLoadingState = (store) => store.chats.activeChat.loading;
export const getActiveChatBtnAnimationState = (store) => store.chats.activeChat.submitBtnAnimation;
export const getActiveTypewriterState = (store) => store.chats.activeChat.typewriterInProgress;
export const getChatId = (store) => store.chats.activeChat.chatId;
export const getUserChatId = (store) => store.chats.activeChat.userID;
export const getAnswerIntoViewScrollTrigger = (store) => store.chats.scrollTrigger;
export const getAdvisorInfo = (store) => store.chats.activeChat.advisor;
export const getChatHeaderCounter = (store) => store.chats.activeChat.chatHeaderCounter;
export const getPaymentIssueNotificationState = (store) => store.chats.activeChat.showPaymentIssueNotification;
export const getUserLimitsState = (store) => store.chats.activeChat.showUserLimits;
export const getIsWithTypewriter = (store) => store.chats.activeChat.isWithTypewriter;
export const getTarotFollowupPending = (store) => store.chats.activeChat.tarotFollowupPending;
export const getUnreadMessagesTriggerId = (store) => store.chats.activeChat.unreadMessagesTriggerId;
export const getWssRemovedChatTrigger = (store) => store.chats.activeChat.wasRemovedByOwner;
export const getChatMembersData = (store) => store.chats.activeChat.chatMembersInfo;
export const getChatDeleteModalConfig = (store) => store.chats.chats.chatDeleteModalConfig;
export const getMultiChatModalVisibility = (store) => store.chats.showMultyChatInvationModal;
export const getShorChatInfo = (store) => store.chats.activeChat.chatMembersInfo.data;
export const getNewUnreadMessages = (store) => store.chats.activeChat.unreadMessages;
export const getShowReconnectAnimation = (store) => store.chats.activeChat.showReconnectAnimation;
export const getUserConnectingState = (store) => store.chats.activeChat.userConnecting;
export const getChatLoadedImage = (store) => store.chats.activeChat.loadedImage;
export const getUpdateRemoveChatModalState = (store) => store.chats.updateRemoveChatModal;
export const getAttemptsToChatWithExceededLimit = (store) => store.chats.activeChat.attemptsToChatWithExceededLimit;
export const getMessagePackOffer = (store) => store.chats.activeChat.messagePackOffer;
export const getMessagePackToast = (store) => store.chats.activeChat.messagePackToast;
export const getMessagePackReceipt = (store) => store.chats.activeChat.messagePackReceipt;

const chatsSlice = createSlice({
    name: 'chats',
    initialState,
    reducers: {
        clearChatsError(store) {
            store.error = null;
        },
        triggerAnswerIntoViewScroll(store) {
            store.scrollTrigger = new Date().getTime();
        },
        clearActiveChatError(store) {
            store.activeChat.error = null;
        },
        setTypewriterProgressState(store, { payload }) {
            store.activeChat.typewriterInProgress = payload;
        },
        cleanMessagesForTypewriter(store) {
            store.activeChat.messagesForTypewriter = [];
        },
        updateMessagesForTypewriter(store) {
            store.activeChat.messagesForTypewriter = store.activeChat.messagesForTypewriter.splice(1);
        },
        // HORO-10384: prepend a paired (user, error) bubble to the active
        // chat after a timeout / network failure. The `sendNewChatMessage`
        // rejected reducer below has already trimmed the optimistic user
        // bubble (slice(1)) — we re-add it here with a stable local id
        // alongside a synthetic assistant error bubble so the user sees
        // exactly what they sent and gets a Retry button without losing
        // their message. Stored client-side only — the next
        // fetchChatMessages drain will clear it (the backend never knew
        // about either bubble).
        addInlineSendError(store, { payload }) {
            const { content, attachments, reason } = payload;
            const id = Date.now();
            store.activeChat.messages = [
                {
                    id: `error_${id}`,
                    role: USER_CHAT_ROLE.ASSISTANT,
                    error: reason,
                    pairedUserMessageId: `local_user_${id}`,
                    // Replay payload retained so the Retry handler can re-
                    // dispatch without needing the original DOM/event.
                    replyToContent: content,
                    replyToAttachments: attachments
                },
                {
                    id: `local_user_${id}`,
                    role: USER_CHAT_ROLE.USER,
                    content,
                    attachments,
                    isLocalSendError: true
                },
                ...store.activeChat.messages
            ];
        },
        // HORO-10384: remove a specific inline-error bubble (and optionally
        // its paired local user bubble — the Retry handler clears both
        // before re-dispatch so the chat thread doesn't end up with a
        // stale error sitting above the typing-dots).
        removeInlineSendError(store, { payload }) {
            const { errorId, removePairedUserMessage = false } = payload;
            const errorMsg = store.activeChat.messages.find((m) => m?.id === errorId);
            const pairedId = errorMsg?.pairedUserMessageId;
            store.activeChat.messages = store.activeChat.messages.filter((m) => {
                if (m?.id === errorId) return false;
                if (removePairedUserMessage && pairedId && m?.id === pairedId) return false;
                return true;
            });
        },
        appendNewMessageToChat(store, { payload }) {
            // The same message can arrive via multiple paths (REST drain,
            // WS broadcast, gamification reflows) and there is no single
            // chokepoint that guarantees uniqueness. Cheaper to guard at
            // the reducer than to police every dispatch site.
            //
            // When the id already exists, MERGE rather than skip. Race
            // scenario in the daily-horoscope flow: fetchChatMessages
            // can land a server snapshot of the bot reply before the
            // backend has finished its second save that attaches
            // `payload.cta = { kind: 'set_birthdate', ... }`. The
            // typewriter-dispatched complete copy then arrives via this
            // reducer; a plain skip would leave the chat with a
            // CTA-less bot bubble (no inline button, no auto-popup).
            // The merge below only overwrites with non-null incoming
            // values so a partial later push (e.g., a WS update that
            // omits `payload`) can't blast an existing field back to
            // null.
            const id = payload?.id || payload?._id;
            if (id) {
                const existingIdx = store.activeChat.messages.findIndex((m) => (m?.id || m?._id) === id);
                if (existingIdx !== -1) {
                    const existing = store.activeChat.messages[existingIdx];
                    const merged = { ...existing };
                    for (const key of Object.keys(payload)) {
                        if (payload[key] != null) merged[key] = payload[key];
                    }
                    store.activeChat.messages[existingIdx] = merged;
                    return;
                }
            }
            store.activeChat.messages = [payload, ...store.activeChat.messages];
        },
        setChatHeaderCounter(store, { payload }) {
            store.activeChat.chatHeaderCounter = payload;
        },
        togglePaymentIssueNotification(store, { payload }) {
            store.activeChat.showPaymentIssueNotification = payload;
        },
        setChatDeleteModalConfig(store, { payload }) {
            store.chats.chatDeleteModalConfig = {
                ...store.chats.chatDeleteModalConfig,
                ...payload
            };
        },
        setActiveChatId(store, { payload }) {
            // Unconditional reset so the chat page's auto-send guard always
            // waits for the new chat's fetchChatMessages to complete before
            // firing. A conditional reset (only when chatId changes) breaks
            // the landing-page flow: navigate carries the same chatId that
            // was set moments before, leaving hasLoadedMessages=true and
            // triggering the auto-send before the fetch resolves.
            store.activeChat.hasLoadedMessages = false;
            store.activeChat.chatId = payload;
        },
        setLastMessageInChatId(store, { payload }) {
            store.activeChat.lastMessageInChatId = payload;
        },
        setShowReconnectAnimation(store, { payload }) {
            store.activeChat.showReconnectAnimation = payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchChats.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.chats = {
                    ...store.chats,
                    loading: true,
                    error: null,
                    reloading
                };
            })
            .addCase(fetchChats.fulfilled, (store, { payload }) => {
                const reloading = store.chats.reloading;
                store.chats = {
                    ...store.chats,
                    loading: false,
                    reloading: false,
                    list: reloading ? [...payload.chats] : [...store.chats.list, ...payload.chats],
                    success: payload.success,
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1
                };
            })
            .addCase(fetchChats.rejected, (store, { payload }) => {
                store.chats = {
                    ...store.chats,
                    loading: false,
                    error: payload?.error,
                    success: payload?.success,
                    reloading: false
                };
            })
            .addCase(startNewChat.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startNewChat.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length >= UNREG_CHATS_MAX) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startNewChat.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(startChatWithAdvisor.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startChatWithAdvisor.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length >= UNREG_CHATS_MAX) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startChatWithAdvisor.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(startChatWithAdvisorByActivities.pending, (store) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    error: null
                };
            })
            .addCase(startChatWithAdvisorByActivities.fulfilled, (store, { payload }) => {
                gaEvents.eventStartChat(payload?.advisor?.id);
                const unregristerCatIds = storageService.get(UNREG_CHATS_ID) || [];
                const userToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    dateCreated: payload?.chat?.dateCreated,
                    name: payload?.chat?.name,
                    messages: payload?.messages,
                    advisor: payload?.advisor
                };
                storageService.add(STORAGE_KEY_ACTIVE_CHAT, payload?.id);
                if (unregristerCatIds.length >= UNREG_CHATS_MAX) {
                    unregristerCatIds.shift();
                }
                if (!userToken) {
                    storageService.add(UNREG_CHATS_ID, [...unregristerCatIds, payload?.id]);
                }
            })
            .addCase(startChatWithAdvisorByActivities.rejected, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    error: payload
                };
            })
            .addCase(fetchChatMessages.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    reloading: reloading,
                    error: null,
                    isWithTypewriter: true,
                    ...(reloading && { advisor: null })
                };
            })
            .addCase(fetchChatMessages.fulfilled, (store, { payload }) => {
                const reloading = store.activeChat.reloading;
                const advisor = payload.messages.find((message) => message.role === USER_CHAT_ROLE.ASSISTANT);
                const getFilteredMessages = () => {
                    const newList = payload.messages.filter((newobj) => {
                        return store.activeChat.messages.filter((oldObj) => oldObj.id === newobj.id).length === 0;
                    });
                    return [...store.activeChat.messages, ...newList.reverse()];
                };

                // If a sendNewChatMessage is in-flight (SEND_MESSAGE_ID present),
                // re-attach the pending bubble after the reload so it isn't lost.
                // Keep loading=true so the "Veda is typing" animation and send-button
                // block persist until the AI response arrives — without this, the fast
                // fetch would clear loading before sendNewChatMessage.fulfilled fires
                // (critical for the landing-page flow where the send is dispatched
                // before navigation and fetchChatMessages races it on the new page).
                // Also reset typewriter state so the ChatList effect can fire when
                // sendNewChatMessage.fulfilled arrives.
                const pendingSend = store.activeChat.messages.find((m) => m.id === SEND_MESSAGE_ID);
                const serverMessages = reloading ? [...payload.messages.reverse()] : getFilteredMessages();
                // When the backend has already persisted the pending user message (it saves
                // to DB before awaiting the AI response), the server list contains a real
                // copy of the same content. Filter it out so the user doesn't see their
                // message 2–3× after navigation.
                const serverMessagesDeduped = pendingSend
                    ? serverMessages.filter((m) => !(m.role === USER_CHAT_ROLE.USER && m.content === pendingSend.content))
                    : serverMessages;
                // Position the pending bubble correctly inside the newest-first array.
                // Naively prepending it (the old behaviour) put the *user* message at
                // index 0 even when the bot's reply was already at serverMessagesDeduped[0],
                // inverting visual chat order until a hard reload. The user's `Daily
                // horoscope` bubble would appear BELOW the bot's "I need your birthdate"
                // reply in column-reverse rendering because the array was effectively
                // oldest-first. With the bot reply already on the server, pendingSend
                // belongs at index 1 (one slot older than the bot reply); when the bot
                // reply isn't on the server yet, pendingSend is the newest thing in the
                // chat and goes at index 0.
                let finalMessages;
                if (reloading && pendingSend) {
                    if (serverMessagesDeduped.length > 0) {
                        finalMessages = [serverMessagesDeduped[0], pendingSend, ...serverMessagesDeduped.slice(1)];
                    } else {
                        finalMessages = [pendingSend];
                    }
                } else {
                    finalMessages = serverMessages;
                }

                store.activeChat = {
                    ...store.activeChat,
                    loading: !!pendingSend,
                    reloading: false,
                    name: payload?.name,
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1,
                    messages: finalMessages,
                    userID: payload?.userID,
                    advisor: advisor?.advisor || null,
                    anchorMessageId: payload.messages[payload.messages.length - 1]?.id,
                    hasLoadedMessages: true,
                    ...(pendingSend && { typewriterInProgress: false, submitBtnAnimation: false })
                };
            })
            .addCase(fetchChatMessages.rejected, (store, payload) => {
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    reloading: false,
                    error: payload
                };
            })
            .addCase(sendNewChatMessage.pending, (store, { meta: { arg } }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), false, {
                    content: arg.content,
                    attachments: arg.attachments
                });
                // HORO-10384: sweep any prior client-side failure pairs
                // (`addInlineSendError` adds an error bubble + a synthetic
                // local_user_* bubble for each timeout / network failure).
                // Without this, hammering Retry or re-typing the same
                // message stacks one pair per attempt and the thread fills
                // with duplicate user bubbles + stale error bubbles. The
                // contract is "only the latest failed attempt is visible" —
                // a fresh send starts from a clean thread.
                const sweptMessages = store.activeChat.messages.filter((m) => !m?.error && !m?.isLocalSendError);
                store.activeChat = {
                    ...store.activeChat,
                    loading: true,
                    submitBtnAnimation: true,
                    typewriterInProgress: true,
                    error: null,
                    isWithTypewriter: true,
                    messages: [
                        {
                            content: arg.content,
                            attachments: arg.attachments,
                            role: USER_CHAT_ROLE.USER,
                            id: SEND_MESSAGE_ID
                        },
                        ...sweptMessages
                    ]
                };
            })
            .addCase(sendNewChatMessage.fulfilled, (store, { payload }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), false, '', true);
                const replyTo = payload.answer.find((item) => item.role === USER_CHAT_ROLE.USER);
                const filteredAnswers = payload.answer.filter((item) => item.role !== USER_CHAT_ROLE.USER);
                const plan = payload?.plan;
                const isPaymentIssue = plan ? plan?.isFree && plan?.isTrial && !plan?.isCancel : false;
                const isUserLimits = plan ? plan?.isFree : true;
                const formattedMessages = store.activeChat.messages.map((item) => {
                    if (item.id === SEND_MESSAGE_ID) {
                        item.id = replyTo.id;
                    }
                    return item;
                });
                store.activeChat = {
                    ...store.activeChat,
                    messages: formattedMessages,
                    loading: false,
                    submitBtnAnimation: false,
                    // Reset so the ChatList typewriter-start effect can fire.
                    // The WS path (multi-user chats) never delivers messages
                    // back to the sender — `chatModel.sendMessage` self-excludes
                    // the fromId — so the AI response only reaches the sender
                    // via `messagesForTypewriter`. Keeping `typewriterInProgress`
                    // true (inherited from pending) permanently blocks that path.
                    typewriterInProgress: false,
                    messagesForTypewriter: [...filteredAnswers],
                    showPaymentIssueNotification: isPaymentIssue,
                    showUserLimits: isUserLimits
                };
            })
            .addCase(requestTarotFollowup.pending, (store) => {
                // Set while the backend is generating the follow-up so the
                // chat can show a typing-dots placeholder bubble.
                store.activeChat.tarotFollowupPending = true;
            })
            .addCase(requestTarotFollowup.fulfilled, (store, { payload }) => {
                store.activeChat.tarotFollowupPending = false;
                // Route through the typewriter queue so the follow-up
                // types in character-by-character — same path Veda's
                // normal responses take. The ChatList auto-append effect
                // moves the head of `messagesForTypewriter` into
                // `messages` and starts the typewriter when no other
                // typewriter is running; if one is, the queue advances
                // when the current message finishes. Append (not replace)
                // so any in-flight typewriter from a regular send isn't
                // clobbered.
                const incoming = Array.isArray(payload?.answer) ? payload.answer : [];
                if (incoming.length === 0) return;
                store.activeChat.isWithTypewriter = true;
                store.activeChat.messagesForTypewriter = [...store.activeChat.messagesForTypewriter, ...incoming];
            })
            .addCase(requestTarotFollowup.rejected, (store) => {
                store.activeChat.tarotFollowupPending = false;
            })
            .addCase(requestBirthChartFollowup.pending, (store) => {
                store.activeChat.tarotFollowupPending = true;
            })
            .addCase(requestBirthChartFollowup.fulfilled, (store, { payload }) => {
                store.activeChat.tarotFollowupPending = false;
                const incoming = Array.isArray(payload?.answer) ? payload.answer : [];
                if (incoming.length === 0) return;
                store.activeChat.isWithTypewriter = true;
                store.activeChat.messagesForTypewriter = [...store.activeChat.messagesForTypewriter, ...incoming];
            })
            .addCase(requestBirthChartFollowup.rejected, (store) => {
                store.activeChat.tarotFollowupPending = false;
            })
            .addCase(sendNewChatMessage.rejected, (store, { payload, meta: { arg } }) => {
                updateResendData(storageService.get(STORAGE_KEY_ACTIVE_CHAT), true, {
                    content: arg.content,
                    attachments: arg.attachments
                });
                store.activeChat = {
                    ...store.activeChat,
                    loading: false,
                    submitBtnAnimation: false,
                    typewriterInProgress: false,
                    error: payload,
                    messages: [...store.activeChat.messages.slice(1)]
                };
            })
            .addCase(deleteChat.fulfilled, (store, { payload }) => {
                if (payload.status.success !== true) {
                    return;
                }

                store.chats.list = store.chats.list.filter((chat) => {
                    if (chat.id === payload.chatId) {
                        return;
                    }
                    return chat;
                });
            })
            .addCase(editChatName.fulfilled, (store, { payload }) => {
                store.chats.list = store.chats.list.map((chat) => {
                    if (chat.id === payload.chat.id) {
                        chat.name = payload.chat.name;
                    }
                    return chat;
                });
            })
            .addCase(fetchUser.fulfilled, (store, { payload }) => {
                store.activeChat = {
                    ...store.activeChat,
                    showUserLimits: payload?.plan?.isFree
                };
            })
            .addCase(messageAddedInChat, (store, { payload }) => {
                const isArray = Array.isArray(payload);
                const messagesToArray = isArray ? payload : [payload];
                const objIds = store.activeChat.messages.map((item) => item.id || item._id);

                const filteredArray = messagesToArray.filter((item) => !objIds.includes(item.id || item._id));

                if (!store.activeChat.unreadMessagesTriggerId && filteredArray.length > 0) {
                    store.activeChat.unreadMessagesTriggerId = filteredArray[0].id || filteredArray[0]._id;
                }

                if (filteredArray.length > 0) {
                    store.activeChat.unreadMessages = [...filteredArray, ...store.activeChat.unreadMessages];
                }

                store.activeChat.isWithTypewriter = false;
                store.activeChat.messages = [...filteredArray.reverse(), ...store.activeChat.messages];
            })
            .addCase(resetUnreadMessagesTriggerId, (store) => {
                store.activeChat.unreadMessagesTriggerId = null;
                store.activeChat.unreadMessages = [];
            })
            .addCase(chatWasRemovedByOwner, (store, { payload }) => {
                store.activeChat.wasRemovedByOwner = payload;
            })
            .addCase(setMultyChatInvationModalVisibility, (store, { payload }) => {
                store.showMultyChatInvationModal = payload;
            })
            .addCase(connectUserToChat.pending, (store) => {
                store.activeChat.userConnecting = true;
            })
            .addCase(connectUserToChat.fulfilled, (store) => {
                store.activeChat.userConnecting = false;
            })
            .addCase(connectUserToChat.rejected, (store) => {
                store.activeChat.userConnecting = false;
            })
            .addCase(loadImageFile.pending, (store) => {
                store.activeChat.loadedImage.loading = true;
            })
            .addCase(loadImageFile.fulfilled, (store, { payload }) => {
                store.activeChat.loadedImage.loading = false;
                store.activeChat.loadedImage.data = payload;
            })
            .addCase(loadImageFile.rejected, (store) => {
                store.activeChat.loadedImage.loading = false;
            })
            .addCase(resetImageFile, (store) => {
                store.activeChat.loadedImage = {
                    loading: false,
                    data: null
                };
            })
            .addCase(setUpdateRemoveChatModal, (store, { payload }) => {
                store.updateRemoveChatModal = { isOpen: payload.isOpen, alias: payload.alias };
            })
            .addCase(attemptToChatWithExceededLimit, (store) => {
                store.activeChat.attemptsToChatWithExceededLimit += 1;
            })
            .addCase(showMessagePackOffer, (store, { payload }) => {
                if (!payload?.chatId) return;
                store.activeChat.messagePackOffer = { chatId: payload.chatId, at: Date.now() };
            })
            .addCase(clearMessagePackOffer, (store) => {
                store.activeChat.messagePackOffer = null;
            })
            .addCase(setMessagePackToast, (store, { payload }) => {
                store.activeChat.messagePackToast = !!payload;
            })
            .addCase(setMessagePackReceipt, (store, { payload }) => {
                if (!payload?.chatId) return;
                // `anchorMessageId` is which message the receipt hangs under.
                // Dropping it here (as this reducer once did) silently sends
                // the receipt to the pinned fallback slot forever, however
                // correctly it was captured and stored.
                store.activeChat.messagePackReceipt = {
                    chatId: payload.chatId,
                    anchorMessageId: payload.anchorMessageId || null
                };
            })
            .addCase(clearMessagePackReceipt, (store) => {
                store.activeChat.messagePackReceipt = null;
            });
    }
});

export const chatsReducer = chatsSlice.reducer;
export const {
    clearChatsError,
    triggerAnswerIntoViewScroll,
    clearActiveChatError,
    cleanMessagesForTypewriter,
    updateMessagesForTypewriter,
    appendNewMessageToChat,
    addInlineSendError,
    removeInlineSendError,
    setTypewriterProgressState,
    setChatHeaderCounter,
    togglePaymentIssueNotification,
    setChatDeleteModalConfig,
    setActiveChatId,
    setLastMessageInChatId,
    setShowReconnectAnimation
} = chatsSlice.actions;
